import type { AxiosError } from 'axios';

import { getSorErrorCode, getSorErrorTitle } from '~/components/SystemOfRecord/sorErrors';
import { getNotifyUserMessage } from '~/Utils/errors/notifyUserMessage';

interface ErrorResponse {
  data?: {
    message?: string;
    reasons?: string[];
  };
  status?: number;
}

interface ExtractedError {
  title: string;
  message: string;
  reportError: boolean;
}

type ErrorData = { message?: string; reasons?: string[] };

const isClaimNotFoundError = (error: AxiosError<ErrorData>): false | boolean | undefined =>
  error?.response?.status === 404 && error?.response?.data?.message?.startsWith('Claim not found');

const appendReasons = (message: string, reasons: string[] | undefined): string =>
  reasons && reasons.length > 0 ? `${message}: ${reasons.join('; ')}` : message;

export const extractError = (error: AxiosError<ErrorData>): ExtractedError => {
  const response = error.response as ErrorResponse;
  const message = response?.data?.message;
  const reasons = response?.data?.reasons;

  if (
    response &&
    (message === 'User is not authorized to perform this operation' ||
      message?.match(/Role type .* not authorized for function .*/))
  ) {
    return {
      title: 'Authorization Error',
      message: 'User is not authorized to perform this operation.',
      reportError: false,
    };
  }

  // Notify User Specific Errors
  const notifyUserMessage = getNotifyUserMessage(error);
  if (notifyUserMessage !== null) {
    // An action the organization commits at its System of Record: titled by what the SoR did with it
    const sorErrorCode = getSorErrorCode(error);
    return {
      title: sorErrorCode ? getSorErrorTitle(sorErrorCode) : 'Error',
      message: appendReasons(notifyUserMessage, reasons),
      reportError: false,
    };
  }

  // Claim Not Found
  if (isClaimNotFoundError(error)) {
    return {
      title: 'Claim Not Found',
      message: 'Please verify the url',
      reportError: false,
    };
  }

  // Intent-blocked mutations (and other structured errors carrying a reasons list)
  if (message && reasons && reasons.length > 0) {
    return {
      title: 'Error',
      message: appendReasons(message, reasons),
      reportError: false,
    };
  }

  // Rate limited by the edge. Cloud Armor answers a throttled request with its own
  // HTML body, so nothing above matches and the request would otherwise be reported
  // as an unexpected error — one dialog and one Sentry event per denied request, for
  // the whole ban window. It is a deliberate, self-clearing block, so tell the user
  // that and keep it out of Sentry.
  if (response?.status === 429) {
    return {
      title: 'Too Many Requests',
      message: 'Too many requests were sent from this account. Please wait a few minutes and try again.',
      reportError: false,
    };
  }

  // Generic Unexpected Error
  return {
    title: 'Unexpected Error',
    message: 'Unfortunately an unexpected error has occurred. We are working to solve it.',
    reportError: true,
  };
};
